import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Typography,
  message,
  Spin,
  Row,
  Col,
  Divider,
  Switch,
} from "antd";
import { ArrowLeftOutlined, SaveOutlined } from "@ant-design/icons";

import { cinemaHallsApi, cinemasApi, myLibraryApi, subscriptionTypesApi } from "../services/api";
import type { Cinema, CinemaHall } from "../types";
import { RatingSelect } from "../components/RatingSelect";
import { WorkInfoCard } from "../components/WorkInfoCard";
import { usePermissions } from "../hooks/usePermissions";

const { Title, Text } = Typography;

export function MyLibraryEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const { canEdit } = usePermissions();
  const isReadOnly = !canEdit;

  const { data: entry, isLoading: entryLoading } = useQuery({
    queryKey: ["my-library-entry", id],
    queryFn: () => myLibraryApi.getEntry(id as string),
    enabled: !!id,
  });

  const { data: cinemasData } = useQuery({
    queryKey: ["cinemas", "my-library-edit"],
    queryFn: () => cinemasApi.list({ page: 1, page_size: 1000 }),
    enabled: entry?.work_type === "movie",
  });

  const cinemaOptions = useMemo(() => {
    const items = cinemasData?.items ?? [];
    return items.map((c: Cinema) => ({
      value: c.id,
      label: c.city ? `${c.name} (${c.city})` : c.name,
    }));
  }, [cinemasData]);

  const selectedCinemaId = Form.useWatch("cinema_id", form);

  const { data: hallsData, isLoading: hallsLoading } = useQuery({
    queryKey: ["cinema-halls", selectedCinemaId],
    queryFn: () => cinemaHallsApi.listByCinema(selectedCinemaId as string),
    enabled: !!selectedCinemaId,
  });

  const hallOptions = useMemo(() => {
    const items = hallsData ?? [];
    return items.map((h: CinemaHall) => ({ value: h.id, label: h.hall_name }));
  }, [hallsData]);

  const { data: subscriptionTypesData, isLoading: subscriptionTypesLoading } = useQuery({
    queryKey: ["subscription-types"],
    queryFn: () => subscriptionTypesApi.list(),
    enabled: entry?.work_type === "movie",
  });

  const subscriptionTypeOptions = useMemo(() => {
    const items = subscriptionTypesData?.items ?? [];
    return items
      .filter((st) => st.is_active)
      .map((st) => ({ value: st.id, label: st.name }));
  }, [subscriptionTypesData]);

  useEffect(() => {
    if (entry) {
      form.setFieldsValue({
        finished_at: entry.finished_at,
        rating: entry.rating,
        review: entry.review,
        private_notes: entry.private_notes,
        is_public: entry.is_public,
        storage_location: entry.storage_location,
        price: entry.price,
        cinema_id: entry.cinema_id || undefined,
        cinema_hall_id: entry.cinema_hall_id || undefined,
        screening_at: entry.screening_at
          ? new Date(entry.screening_at).toISOString().slice(0, 16)
          : undefined,
        ticket_price: entry.ticket_price,
        is_subscription: entry.is_subscription,
        subscription_type_id: entry.subscription_type_id,
        seat_row: entry.seat_row,
        seat_number: entry.seat_number,
      });
    }
  }, [entry, form]);

  const mutation = useMutation({
    mutationFn: (payload: any) =>
      myLibraryApi.updateEntry(id as string, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-library-years"] });
      queryClient.invalidateQueries({ queryKey: ["my-library-stats"] });
      queryClient.invalidateQueries({ queryKey: ["my-library-monthly"] });
      queryClient.invalidateQueries({ queryKey: ["my-library-entries"] });
      queryClient.invalidateQueries({ queryKey: ["my-library-entry", id] });
      message.success("Wpis zaktualizowany");
      navigate("/my-library/entries");
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.detail || "Nie udało się zaktualizować wpisu";
      message.error(errorMessage);
    },
  });

  if (entryLoading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!entry) {
    return <div>Nie znaleziono wpisu</div>;
  }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/my-library/entries")}
          style={{ padding: 0 }}
        >
          Powrót do listy
        </Button>
        <Title level={2} style={{ marginTop: 8, marginBottom: 0 }}>
          Edytuj wpis: {entry.work_title}
        </Title>
        <Text type="secondary">
          {entry.work_type === "book"
            ? "Książka"
            : entry.work_type === "movie"
              ? "Film"
              : "Serial"}
          {entry.season_number ? ` (Sezon ${entry.season_number})` : ""}
        </Text>
      </div>

      <WorkInfoCard workId={entry.work_id} />

      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => {
            const payload: any = {
              finished_at: values.finished_at,
              rating: values.rating,
              review: values.review,
              private_notes: values.private_notes,
              is_public: values.is_public,
            };

            if (entry.work_type === "book") {
              payload.storage_location = values.storage_location;
              payload.price = values.price;
            }

            if (entry.work_type === "movie") {
              payload.cinema_id = values.cinema_id;
              payload.cinema_hall_id = values.cinema_hall_id;
              payload.screening_at = values.screening_at
                ? new Date(values.screening_at).toISOString()
                : null;
              payload.ticket_price = values.ticket_price;
              payload.is_subscription = values.is_subscription;
              payload.subscription_type_id = values.subscription_type_id;
              payload.seat_row = values.seat_row;
              payload.seat_number = values.seat_number;
            }

            mutation.mutate(payload);
          }}
        >
          <Form.Item
            name="is_public"
            label="Widoczny publicznie"
            valuePropName="checked"
            tooltip="Gdy włączone, ten wpis będzie widoczny w publicznej bibliotece"
          >
            <Switch checkedChildren="Tak" unCheckedChildren="Nie" />
          </Form.Item>

          <Divider />

          <Form.Item
            name="finished_at"
            label="Data ukończenia"
            rules={[{ required: true, message: "Data jest wymagana" }]}
          >
            <Input type="date" />
          </Form.Item>

          {entry.work_type === "movie" && (
            <>
              <Title level={4}>Dane seansu</Title>
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item name="cinema_id" label="Kino">
                    <Select
                      placeholder="Wybierz kino"
                      options={cinemaOptions}
                      allowClear
                      onChange={() =>
                        form.setFieldValue("cinema_hall_id", undefined)
                      }
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="cinema_hall_id" label="Sala">
                    <Select
                      placeholder={
                        selectedCinemaId
                          ? "Wybierz salę"
                          : "Najpierw wybierz kino"
                      }
                      options={hallOptions}
                      loading={hallsLoading}
                      disabled={!selectedCinemaId}
                      allowClear
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item name="screening_at" label="Data i godzina seansu">
                    <Input type="datetime-local" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="ticket_price" label="Cena biletu">
                    <InputNumber min={0} step={0.5} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="is_subscription"
                    label="Abonament"
                    valuePropName="checked"
                  >
                    <Switch checkedChildren="Tak" unCheckedChildren="Nie" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) =>
                      prevValues.is_subscription !== currentValues.is_subscription
                    }
                  >
                    {({ getFieldValue }) =>
                      getFieldValue("is_subscription") ? (
                        <Form.Item name="subscription_type_id" label="Typ abonamentu">
                          <Select
                            placeholder="Wybierz typ abonamentu"
                            options={subscriptionTypeOptions}
                            loading={subscriptionTypesLoading}
                            style={{ width: "100%" }}
                            allowClear
                          />
                        </Form.Item>
                      ) : null
                    }
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item name="seat_row" label="Rząd">
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="seat_number" label="Miejsce">
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
              <Divider />
            </>
          )}

          <Form.Item name="rating" label="Ocena">
            <RatingSelect />
          </Form.Item>

          <Form.Item name="review" label="Notatka">
            <Input.TextArea rows={4} />
          </Form.Item>

          <Form.Item name="private_notes" label="Prywatne notatki">
            <Input.TextArea rows={4} />
          </Form.Item>

          {entry.work_type === "book" && (
            <>
              <Form.Item name="storage_location" label="Miejsce składowania">
                <Input />
              </Form.Item>
              <Form.Item name="price" label="Cena zakupu">
                <InputNumber
                  min={0}
                  step={0.01}
                  style={{ width: 200 }}
                  addonAfter="zł"
                />
              </Form.Item>
            </>
          )}

          <Form.Item
            style={{ marginTop: 24, marginBottom: 0, textAlign: "right" }}
          >
            <Space>
              <Button onClick={() => navigate("/my-library/entries")}>
                Anuluj
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={mutation.isPending}
                disabled={isReadOnly}
                title={isReadOnly ? "Brak uprawnień do edycji" : undefined}
              >
                Zapisz zmiany
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}

export default MyLibraryEditPage;
